import pdfMake from 'pdfmake/build/pdfmake'
import * as vfsFonts from 'pdfmake/build/vfs_fonts'

import logoImage from '@/assets/pdf-logo.png'
import {
  BASIC_MARGINS,
  BODY_FONT_SIZE,
  HEADERS_KEYS,
  HEADER_FONT_SIZE,
  HEADER_MARGINS,
  LAYOUTS,
  LAYOUT_TYPES,
  SECTION_HEADER_FONT_SIZE,
  SUMMARY_MARGINS,
  TABLE_BODY_MARGIN,
  TABLE_HEADERS_FONT_SIZE,
  TABLE_HEADERS_MARGIN,
  getHeaders
} from '@/helpers'
import i18n from '@/plugins/i18n'

// Configure pdfMake fonts
pdfMake.vfs = vfsFonts.pdfMake ? vfsFonts.pdfMake.vfs : pdfMake.vfs

const HEADER_BACKGROUND_COLOR = '#DCE6F1'
const PROJECT_BACKGROUND_COLOR = '#BFBFBF'
const TABLE_BACKGROUND_COLOR = '#FFF'

const convertImageToBase64 = async (imageUrl) => {
  try {
    const response = await fetch(imageUrl)
    const blob = await response.blob()
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (err) {
    console.error('Error fetching the image', err)
    return null
  }
}

const formatCell = (
  text,
  fontSize,
  margin,
  fillColor,
  bold = false,
  alignment = 'left'
) => ({
  text,
  fontSize,
  margin,
  fillColor,
  bold,
  alignment
})

const formatCellWithImage = (
  image,
  width = 120,
  margin = [0, 0, 0, 0],
  alignment = 'left',
  fillColor = null
) => ({
  image,
  width,
  margin,
  alignment,
  fillColor
})

const createTable = ({
  headers = null,
  data,
  footer = [],
  layout = LAYOUT_TYPES.DEFAULT,
  width = '*',
  headerColor = HEADER_BACKGROUND_COLOR,
  tableColor = TABLE_BACKGROUND_COLOR,
  extendDescription = true
} = {}) => {
  if (data.length === 0) return null

  const formattedData = data.map((row) =>
    row.map((cell) => formatCell(cell, BODY_FONT_SIZE, TABLE_BODY_MARGIN, tableColor))
  )

  if (headers) {
    const formattedHeaders = headers.map((header) =>
      formatCell(header, TABLE_HEADERS_FONT_SIZE, TABLE_HEADERS_MARGIN, headerColor)
    )
    formattedData.unshift(formattedHeaders)
  }

  if (footer.length > 0) {
    const formattedFooter = footer.map((footerCell, index) => {
      if (index === 1 && extendDescription) {
        return {
          ...formatCell(footerCell, BODY_FONT_SIZE, TABLE_BODY_MARGIN, headerColor),
          colSpan: 2
        }
      }
      return formatCell(footerCell, BODY_FONT_SIZE, TABLE_BODY_MARGIN, headerColor)
    })

    formattedData.push(formattedFooter)
  }

  const columnCount = headers ? headers.length : data[0].length
  const widths = new Array(columnCount).fill(width).map((w, index) => {
    if (index === 0) return 80
    if (index === 1) return 80
    return w
  })

  return {
    table: {
      widths,
      body: formattedData
    },
    layout: LAYOUTS[layout] || LAYOUTS.default
  }
}

const createSimpleTable = ({
  data,
  layout = LAYOUT_TYPES.DEFAULT,
  backgroundColor = HEADER_BACKGROUND_COLOR,
  fontSize = BODY_FONT_SIZE,
  margin = TABLE_BODY_MARGIN,
  bold = false,
  alignment = 'left',
  width = '*',
  extendDescription = true,
  colSpan = 2
} = {}) => {
  const formattedData = data.map((row) => {
    const hasImage = row.some(
      (cell) => cell && typeof cell === 'object' && 'image' in cell
    )

    if (hasImage) {
      return row.map((cell) => {
        if (cell && typeof cell === 'object' && 'image' in cell) {
          return formatCellWithImage(
            cell.image,
            cell.width,
            cell.margin,
            cell.alignment,
            backgroundColor
          )
        }
        return formatCell(
          cell,
          fontSize,
          margin,
          backgroundColor,
          bold,
          alignment
        )
      })
    }

    return row.reduce((acc, cell, index) => {
      if (index === 1 && extendDescription) {
        acc.push({
          ...formatCell(
            cell,
            fontSize,
            margin,
            backgroundColor,
            bold,
            alignment
          ),
          colSpan
        })
      } else {
        acc.push(
          formatCell(
            cell,
            fontSize,
            margin,
            backgroundColor,
            bold,
            alignment
          )
        )
      }
      return acc
    }, [])
  })

  return {
    table: {
      widths: new Array(data[0].length).fill(width),
      body: formattedData
    },
    layout: LAYOUTS[layout] || LAYOUTS.default
  }
}

const createAlternatingTable = (
  data,
  primaryColor = HEADER_BACKGROUND_COLOR,
  secondaryColor = '#fff',
  layout = LAYOUT_TYPES.DEFAULT,
  width = '*'
) => {
  const formattedData = data.map((row) =>
    row.map((cell, cellIndex) =>
      formatCell(cell, BODY_FONT_SIZE, TABLE_BODY_MARGIN, cellIndex % 2 === 0 ? primaryColor : secondaryColor)
    )
  )

  const widths = new Array(data[0].length).fill(width).map((w, index) => {
    if (index === 0) return 70
    if (index === 2) return 70
    return w
  })

  return {
    table: {
      widths,
      body: formattedData
    },
    layout: LAYOUTS[layout] || LAYOUTS.default
  }
}

const createFramedTable = (innerTable, margin = 20) => ({
  table: {
    widths: ['*'],
    body: [
      [
        {
          stack: [innerTable],
          margin: [margin, margin, margin, margin],
          border: [true, true, true, true],
          fillColor: '#FFFFFF'
        }
      ]
    ]
  },
  layout: {
    defaultBorder: false
  }
})

const createSectionHeader = (
  text,
  pageBreak = false,
  pageOrientation = 'portrait',
  backgroundColor = HEADER_BACKGROUND_COLOR,
  borderColor = '#000000',
  layout
) => ({
  table: {
    widths: ['*'],
    body: [
      [
        {
          text,
          fontSize: SECTION_HEADER_FONT_SIZE,
          margin: BASIC_MARGINS,
          fillColor: backgroundColor,
          border: [true, true, true, true],
          borderColor
        }
      ]
    ]
  },
  layout: {
    layout: LAYOUTS[layout] || LAYOUTS.default
  },
  pageOrientation,
  pageBreak
})

const createSignatureField = (title) => ({
  stack: [
    { text: title, bold: true, margin: [0, 0, 0, 20] },
    { text: 'Signature', margin: [0, 20, 0, 20] },
    { text: '', margin: [0, 0, 0, 20], border: [true, false, true, false] },
    { text: 'Date', margin: [0, 20, 0, 20] },
    { text: '', margin: [0, 0, 0, 20], border: [true, false, true, false] }
  ],
  height: 300
})

const createFramedSignatureSection = () => ({
  table: {
    widths: ['*'],
    body: [
      [
        {
          table: {
            widths: ['*', '*'],
            body: [
              [
                createSignatureField(i18n.t('payment.certificate.employerRepresentative', { number: 1 })),
                createSignatureField(i18n.t('payment.certificate.employerRepresentative', { number: 2 }))
              ]
            ]
          },
          layout: {
            defaultBorder: false
          },
          margin: [20, 20, 20, 20],
          border: [true, true, true, true],
          fillColor: '#FFFFFF',
          height: 300
        }
      ]
    ]
  }
})

const sumValuesByKey = (data) => data.reduce((acc, curr) => {
  Object.keys(curr).forEach((key) => {
    if (typeof curr[key] === 'number') {
      acc[key] = (acc[key] || 0) + curr[key]
    }
  })
  return acc
}, {})

const createTotal = (data, title) => {
  if (data.length === 0) return null

  const summedValues = sumValuesByKey(data)

  return [
    '',
    title,
    '',
    formatValue(summedValues.totalCents) || 0,
    formatValue(summedValues.cumulativeCents) || 0,
    formatValue(summedValues.cumulativePreviousCents) || 0,
    formatValue(summedValues.paymentCents) || 0
  ]
}

const formatValue = (value) => {
  if (typeof value === 'number') {
    const formattedValue = (value / 100).toString()
    return formattedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  }
  return value
}

export const createPaymentCertificatePdf = async (data) => {
  const currentDate = new Date().toLocaleDateString()
  const base64Image = await convertImageToBase64(logoImage)

  const summaryHeaders = Object.keys(data.summary[0]).filter(
    (header) => header !== 'key'
  )
  const extractItemsContent = (data) => data.map((detail) =>
    detail.items.map((item) => item.content)
  )
  const details = extractItemsContent(data.details)

  const summary = data.summary.map((item) =>
    summaryHeaders.map((header) => formatValue(item[header]))
  )
  const valueOfWorks = data.valueOfWorks.map((item) =>
    HEADERS_KEYS.map((header) => formatValue(item[header]))
  )
  const variations = data.variations.map((item) =>
    HEADERS_KEYS.map((header) => formatValue(item[header]))
  )
  const claims = data.claims.map((item) =>
    HEADERS_KEYS.map((header) => formatValue(item[header]))
  )
  const amendments = data.amendments.map((item) =>
    HEADERS_KEYS.map((header) => formatValue(item[header]))
  )
  const deductions = data.deductions.map((item) =>
    HEADERS_KEYS.map((header) => formatValue(item[header]))
  )

  const valueOfWorksTotal = createTotal(data.valueOfWorks, i18n.t('payment.certificate.valueOfWorkExecuted'))
  const variationsTotal = createTotal(data.variations, i18n.t('payment.certificate.valueOfVariation'))
  const claimsTotal = createTotal(data.claims, i18n.t('payment.certificate.valueOfClaims'))
  const amendmentsTotal = createTotal(data.amendments, i18n.t('payment.certificate.valueOfAmendments'))
  const deductionsTotal = createTotal(data.deductions, i18n.t('payment.certificate.sumOfDeductions'))

  const totalContractPrice = createTotal(data.contractTotal, i18n.t('payment.certificate.totalContractPrice'))
  const totalAfterDeductions = createTotal(data.totalAfterDeductions, i18n.t('payment.certificate.totalAfterDeductions'))
  const previouslyCertifiedTotal = createTotal(data.previouslyCertifiedTotal, i18n.t('payment.certificate.prevCertifiedAmount'))
  const currentCertificationTotal = createTotal(data.currentCertificationTotal, i18n.t('payment.certificate.currentCertifiedAmount'))

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: [20, 20, 20, 20],
    style: {
      fontSize: 12
    },
    content: [
      createSimpleTable({
        data: [
          [
            {
              image: base64Image,
              margin: [0, 20, 0, 0],
              width: 100,
              alignment: 'center'
            },
            i18n.t('payment.certificate.interimPaymentCertificate')
          ]
        ],
        layout: LAYOUT_TYPES.DEFAULT,
        backgroundColor: HEADER_BACKGROUND_COLOR,
        fontSize: HEADER_FONT_SIZE,
        margin: HEADER_MARGINS,
        bold: true,
        alignment: 'center',
        extendDescription: false
      }),
      createSimpleTable({
        data: [[`Project: ${data.title}`]],
        layout: LAYOUT_TYPES.DEFAULT,
        backgroundColor: PROJECT_BACKGROUND_COLOR,
        fontSize: HEADER_FONT_SIZE,
        margin: BASIC_MARGINS,
        bold: true,
        alignment: 'center',
        extendDescription: false
      }),
      createAlternatingTable(details),
      createFramedTable(
        createTable({
          data: summary,
          tableColor: HEADER_BACKGROUND_COLOR,
          layout: LAYOUT_TYPES.DEFAULT
        })),
      createTable({
        headers: getHeaders(data.contractCurrency),
        data: valueOfWorks,
        footer: valueOfWorksTotal,
        layout: LAYOUT_TYPES.ROW_ONLY_BORDER_WITHOUT_BOTTOM_BORDER,
        extendDescription: false
      }),
      createTable({
        data: variations,
        footer: variationsTotal,
        layout: LAYOUT_TYPES.ROW_ONLY_BORDER_WITHOUT_BOTTOM_BORDER
      }),
      createTable({
        data: claims,
        footer: claimsTotal,
        layout: LAYOUT_TYPES.ROW_ONLY_BORDER_WITHOUT_BOTTOM_BORDER
      }),
      createTable({
        data: amendments,
        footer: amendmentsTotal,
        layout: LAYOUT_TYPES.ROW_ONLY_BORDER_WITHOUT_BOTTOM_BORDER
      }),
      createSimpleTable({
        data: [totalContractPrice],
        layout: LAYOUT_TYPES.ROW_ONLY_BORDER_WITHOUT_BOTTOM_BORDER
      }),
      createTable({
        data: deductions,
        footer: deductionsTotal,
        layout: LAYOUT_TYPES.ROW_ONLY_BORDER_WITHOUT_BOTTOM_BORDER
      }),
      createSimpleTable({
        data: [totalAfterDeductions],
        layout: LAYOUT_TYPES.ROW_ONLY_BORDER_WITHOUT_BOTTOM_BORDER,
        margin: SUMMARY_MARGINS

      }),
      createSimpleTable({
        data: [previouslyCertifiedTotal],
        layout: LAYOUT_TYPES.ROW_ONLY_BORDER_WITHOUT_BOTTOM_BORDER,
        backgroundColor: TABLE_BACKGROUND_COLOR,
        colSpan: 5,
        margin: SUMMARY_MARGINS
      }),
      createSimpleTable({
        data: [currentCertificationTotal],
        layout: LAYOUT_TYPES.ROW_ONLY_BORDER_WITH_BOTTOM_BORDER,
        backgroundColor: TABLE_BACKGROUND_COLOR,
        colSpan: 5,
        margin: SUMMARY_MARGINS
      }),
      createSectionHeader(i18n.t('payment.certificate.employerRepresentatives')),
      createFramedSignatureSection()
    ]
  }

  pdfMake.createPdf(docDefinition).download(`payment-certificate-${currentDate}.pdf`)
}
